/**
 * Pure mappers from a fetched sequence spec (FIR-7659) into the wizard's form
 * shapes, used by the "create sequence from existing" flow.
 *
 * The load-bearing rule: every copied step is emitted WITHOUT an `id`, so on
 * save it routes through EmailBison's create (POST) path. There,
 * `resolveVariantWriteFields` links a copied variant to its original by
 * `variant_from_step` (the original's order in the same request) precisely
 * because the original also has no id — never by `variant_from_step_id`, which
 * would be a stale source id. See docs/solutions/emailbison-variant-write-contract.md.
 *
 * `wait_in_days` is copied verbatim: FirstBite and EmailBison pass it 1:1 today,
 * so the value round-trips; re-translating here would introduce a one-slot shift.
 */

import { groupStepsByFamily } from '../sequenceVariants'

import type {
  SequenceForm,
  SequenceStepForm,
} from '../SequenceTab/SequenceStepFormItem'
import type {
  SequenceSpec,
  SequenceSpecSenderEmail,
} from '../sequence_spec_api'

// Mirror of BisonEmailAccountStatus.CONNECTED in
// server/emailbison/models/email_account.py.
export const CONNECTED_SENDER_STATUS = 'connected'

// Mirror of `defaultStep` in ../SequenceTab/SequenceStepFormItem. Inlined (not
// imported) so this stays a pure module — importing SequenceStepFormItem pulls
// in the Quill editor and a browser `document`, which would couple this logic to
// a DOM environment for no benefit.
const EMPTY_STEP: SequenceStepForm = {
  email_subject: '',
  email_body: '',
  wait_in_days: 1,
}

/**
 * Map a fetched spec into the sequence authoring form, stripping all source step
 * ids and grouping variants adjacent to their original (the authoring model).
 * Falls back to a single empty step when the spec has no sequence.
 */
export function specToSequenceForm(
  spec: SequenceSpec,
  fallbackTitle = ''
): SequenceForm {
  const sequence = spec.sequence
  if (!sequence) {
    return { title: fallbackTitle, steps: [{ ...EMPTY_STEP }] }
  }

  // Group by family using the raw spec steps (which still carry ids +
  // variant_from_step_id), then flatten to [original, ...variants] adjacency —
  // the same transform the normal sequence hydration uses.
  const orderedSteps = groupStepsByFamily(sequence.steps, {
    enabled: true,
  }).flatMap((family) => [
    family.original,
    ...family.variants.map((variant) => variant.step),
  ])

  const steps: SequenceStepForm[] = orderedSteps.map((step) => ({
    // `id` intentionally omitted → copied step creates fresh on save.
    email_subject: step.email_subject,
    email_body: step.email_body,
    wait_in_days: step.wait_in_days,
    is_variant: step.is_variant ?? false,
    // Null the link id: the copy's original has no id either, so the save path
    // derives the order-ref from adjacency. Keeping the stale source id would
    // mis-route the write.
    variant_from_step_id: null,
    active: step.active ?? true,
  }))

  return {
    title: sequence.title || fallbackTitle,
    steps: steps.length > 0 ? steps : [{ ...EMPTY_STEP }],
  }
}

export interface ConnectedSenderSelection {
  /** Ids of senders safe to prefill (connected only). */
  ids: number[]
  /** Senders dropped because they are no longer connected — surfaced to the user. */
  dropped: SequenceSpecSenderEmail[]
}

/**
 * Split the spec's sender refs into connected ids to prefill and dropped
 * (stale) senders to surface. A source inbox disconnected since launch must not
 * be silently attached to the new campaign.
 */
export function extractConnectedSenders(
  spec: SequenceSpec
): ConnectedSenderSelection {
  const ids: number[] = []
  const dropped: SequenceSpecSenderEmail[] = []
  for (const sender of spec.sender_emails) {
    if (sender.status === CONNECTED_SENDER_STATUS) {
      ids.push(sender.id)
    } else {
      dropped.push(sender)
    }
  }
  return { ids, dropped }
}
